import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './global_styles.scss';
import 'bootstrap-icons/font/bootstrap-icons.css';
import App from './App.tsx';
import { guardarPrompt } from '@/lib/instalacao';
import type { EventoInstalacao } from '@/lib/instalacao';

// Monitoramento de erros (Sentry). Liga só se houver DSN no ambiente; sem DSN
// fica inerte. O `environment` separa erros de produção dos de dev no painel.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.PROD ? 'producao' : 'dev',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    // Só erros: sem replay nem tracing (não gasta a cota do plano free).
    sendDefaultPii: false,
  });
}

// Service worker. Existe para o Chrome oferecer "Instalar como app" — ele não
// guarda cache nenhum (ver public/sw.js). Só em produção: em dev, um SW
// registrado atrapalha o hot reload do Vite.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Sem SW o app continua funcionando — perde-se só a instalação.
    });
  });
}

// EM DEV, DESREGISTRA QUALQUER SERVICE WORKER.
//
// O registro acima só acontece em produção, mas o `sw.js` mora em `public/` e o
// Vite o serve igual — basta alguém ter aberto um build de produção neste mesmo
// endereço uma vez para o worker ficar registrado no localhost PARA SEMPRE,
// interceptando tudo o que o servidor de desenvolvimento entrega. Desregistrar
// é seguro: em dev o worker não serve para nada (não há instalação a oferecer,
// e ele não guarda cache).
if ('serviceWorker' in navigator && import.meta.env.DEV) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
}

// O CHROME OFERECE A INSTALAÇÃO UMA VEZ, E CEDO. `beforeinstallprompt` dispara
// logo no carregamento; quem não guardar o evento perde a chance de oferecer o
// botão depois — e é exatamente isso que a tela /public/instalar precisa
// fazer, minutos depois, quando a pessoa chega lá. `preventDefault` só evita a
// barrinha automática do Chrome, que aparece na hora errada.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  guardarPrompt(e as EventoInstalacao);
});

// Campo de valor que recebeu entrada estranha (ver CamposNumericos.tsx) grita por
// este evento em vez de virar zero em silêncio. Aqui a gritaria vira issue no
// Sentry — é como um valor errado chega a nós sem depender de alguém reportar.
// O componente não importa o Sentry de propósito: assim ele continua rodando no
// Node, sem navegador, na bateria de testes.
window.addEventListener('campo-numero-estranho', (e) => {
  const d = (e as CustomEvent).detail as { motivo?: string; recebido?: unknown; campo?: string };
  Sentry.captureMessage(`Campo de valor: ${d?.motivo ?? 'entrada estranha'}`, {
    level: 'warning',
    extra: { recebido: d?.recebido, campo: d?.campo, url: window.location.pathname },
  });
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
